import { canUseIDE, type Plan } from './lib/plans'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useAppStore } from './store/appStore'
import { useAuthStore } from './auth/useAuthStore'
import { AuthPage } from './auth/AuthPage'
import { PlansPage } from './auth/PlansPage'
import { TitleBar } from './components/TitleBar/TitleBar'
import { ActivityBar, type ActivityView } from './components/ActivityBar/ActivityBar'
import { Sidebar } from './components/FileTree/Sidebar'
import { SearchPanel } from './components/SearchPanel/SearchPanel'
import { GitPanel } from './components/GitPanel/GitPanel'
import { CollabPanel } from './components/CollabPanel/CollabPanel'
import { DatabasePanel } from './components/DatabasePanel/DatabasePanel'
import { SecretsPanel } from './components/SecretsPanel/SecretsPanel'
import { PackagePanel } from './components/PackagePanel/PackagePanel'
import { TemplatesPanel } from './components/TemplatesPanel/TemplatesPanel'
import { DeployPanel } from './components/DeployPanel/DeployPanel'
import { VersionHistory } from './components/VersionHistory/VersionHistory'
import { CommunityPage } from './components/Community/CommunityPage'
import { CodeComments } from './components/Comments/CodeComments'
import { BillingDashboard } from './components/BillingDashboard/BillingDashboard'
import { CronPanel } from './components/CronJobs/CronPanel'
import { AnalyticsDashboard } from './components/Analytics/AnalyticsDashboard'
import { ThemeMarketplace } from './components/ThemeMarketplace/ThemeMarketplace'
import { TeamWorkspaces } from './components/TeamWorkspaces/TeamWorkspaces'
import { CodeReviewPanel } from './components/CodeReview/CodeReviewPanel'
import { DocGenPanel } from './components/DocGen/DocGenPanel'
import { SecurityScannerPanel } from './components/SecurityScanner/SecurityScannerPanel'
import { ComposerPanel } from './components/Composer/ComposerPanel'
import { CICDPanel } from './components/CICDPanel/CICDPanel'
import { MarketplacePanel } from './components/Marketplace/MarketplacePanel'
import { ComponentLibraryPanel } from './components/ComponentLibrary/ComponentLibraryPanel'
import { DebuggerPanel } from './components/Debugger/DebuggerPanel'
import ModelComparePanel from './components/ModelCompare/ModelComparePanel'
import ResearchPanel from './components/Research/ResearchPanel'
import { PrivacyPanel } from './components/Privacy/PrivacyPanel'
import { AgentPermissionsPanel } from './components/AgentPermissions/AgentPermissionsPanel'
import { StoragePanel } from './components/Storage/StoragePanel'
import { AutomationsPanel } from './components/Automations/AutomationsPanel'
import { SEOPanel } from './components/SEO/SEOPanel'
import { ValidationPanel } from './components/Validation/ValidationPanel'
import { ArtifactsPanel } from './components/Artifacts/ArtifactsPanel'
import { AgentGoalsPanel } from './components/AgentGoals/AgentGoalsPanel'
import { AQRenderer } from './components/AQAgent/AQRenderer'
import { FigmaImportPanel } from './components/FigmaImport/FigmaImportPanel'
import { FigmaViewer } from './components/FigmaImport/FigmaViewer'
import { CanvasPanel } from './components/Canvas/CanvasPanel'
import { EnterprisePanel } from './components/Enterprise/EnterprisePanel'
import { GenerateAppPanel } from './components/GenerateApp/GenerateAppPanel'
import { VisualBuilderPanel } from './components/VisualBuilder/VisualBuilderPanel'
import { SchemaGenPanel } from './components/SchemaGen/SchemaGenPanel'
import { EditorArea } from './components/Editor/EditorArea'
import { AIPanel } from './components/ChatPanel/AIPanel'
import { StatusBar } from './components/StatusBar/StatusBar'
import { GoalsPage } from './components/Goals/GoalsPage'
import { PreviewPanel } from './components/Preview/PreviewPanel'
import { TerminalPanel } from './components/Terminal/TerminalPanel'
import { ResizablePanel } from './components/Layout/ResizablePanel'
import { CommandPalette, type PaletteMode } from './components/CommandPalette/CommandPalette'
import { SettingsPanel } from './components/Settings/SettingsPanel'
import { ErrorBoundary, CrashTrigger } from './components/ErrorBoundary'
import  LandingPage  from './components/Landing/LandingPage'
import { DashboardPage } from './components/Dashboard/DashboardPage'
import { SharedPanel } from './components/SharedPanel/SharedPanel'
import { PulseMemoryPanel } from './components/PulseMemory/PulseMemoryPanel'

type AppScreen = 'landing' | 'auth' | 'plans' | 'payment' | 'dashboard' | 'ide'
type MobileTab = 'editor' | 'files' | 'ai'

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return mobile
}

function ResizableTerminal() {
  const stored = Number(localStorage.getItem('pulse-terminal-height')) || 250
  const [height, setHeight] = useState(stored)
  const dragging = useRef(false)

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragging.current = true
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }, [])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return
      const bottom = window.innerHeight - e.clientY
      const clamped = Math.min(Math.max(bottom, 80), 600)
      setHeight(clamped)
      localStorage.setItem('pulse-terminal-height', String(clamped))
    }
    const onUp = () => {
      dragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [])

  return (
    <div style={{ height, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
      <div
        onMouseDown={onMouseDown}
        style={{ height: 4, cursor: 'row-resize', background: 'transparent', borderTop: '1px solid var(--border)', flexShrink: 0, transition: 'background 0.15s' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--accent)' }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
      />
      <div style={{ flex: 1, overflow: 'hidden' }}><TerminalPanel /></div>
    </div>
  )
}

export default function App() {
  const { theme, isSidebarOpen, isAIPanelOpen } = useAppStore()
  const user = useAuthStore((s) => s.user)
  const ready = useAuthStore((s) => s.ready)
  const checkSession = useAuthStore((s) => s.checkSession)
  const [screen, setScreen] = useState<AppScreen>('landing')
  const [pendingPrompt, setPendingPrompt] = useState(() => {
    return localStorage.getItem('pulse-pending-prompt') ?? ''
  })
  const pendingPromptRef = useRef(pendingPrompt)
  pendingPromptRef.current = pendingPrompt
  const isMobile = useIsMobile()
  const [mobileTab, setMobileTab] = useState<MobileTab>('editor')

  useEffect(() => { checkSession() }, [checkSession])

  // Global: auto-track file saves in Pulse Memory
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.path) {
        import('./components/PulseMemory/usePulseMemory').then(({ usePulseMemory }) => {
          usePulseMemory.getState().addRecentChange(`Modified ${detail.path.split('/').pop()}`)
        })
      }
    }
    window.addEventListener('pulse:file-saved', handler)
    return () => window.removeEventListener('pulse:file-saved', handler)
  }, [])

  // Handle OAuth redirect (token in URL after Google/GitHub login)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const userStr = params.get('user')
    if (token && userStr) {
      try {
        const parsedUser = JSON.parse(userStr)
        useAuthStore.setState({ token, user: parsedUser, ready: true })
        window.history.replaceState({}, '', window.location.pathname)
      } catch {}
    }
  }, [])

  useEffect(() => {
    if (ready && user) {
      setScreen('dashboard')
      if (pendingPromptRef.current) {
        if (!useAppStore.getState().isAIPanelOpen) {
          useAppStore.getState().toggleAIPanel()
        }
        setPendingPrompt('')
      }
    }
    if (ready && !user) {
      setScreen('landing')
    }
  }, [ready, user])

  const [activeView, setActiveView] = useState<ActivityView>('files')
  const [isPreviewOpen, setPreviewOpen] = useState(false)
  const [isTerminalOpen, setTerminalOpen] = useState(false)

  // Listen for pulse:open-terminal event (from preview Run button)
  useEffect(() => {
    const handler = () => setTerminalOpen(true)
    window.addEventListener('pulse:open-terminal', handler)
    return () => window.removeEventListener('pulse:open-terminal', handler)
  }, [])

  // Listen for pulse:switch-view event (from collab join)
  useEffect(() => {
    const handler = (e: Event) => {
      const view = (e as CustomEvent).detail as ActivityView
      if (view) setActiveView(view)
    }
    window.addEventListener('pulse:switch-view', handler)
    return () => window.removeEventListener('pulse:switch-view', handler)
  }, [])

  // Listen for pulse:open-generate event (from Activity Bar ⚡ button)
  useEffect(() => {
    const handler = () => setGenerateOpen(true)
    window.addEventListener('pulse:open-generate', handler)
    return () => window.removeEventListener('pulse:open-generate', handler)
  }, [])

  // Listen for pulse:open-builder event (from Activity Bar 🎨 button)
  useEffect(() => {
    const handler = () => setBuilderOpen(true)
    window.addEventListener('pulse:open-builder', handler)
    return () => window.removeEventListener('pulse:open-builder', handler)
  }, [])

  const [paletteOpen, setPaletteOpen] = useState(false)
  const [paletteMode, setPaletteMode] = useState<PaletteMode>('commands')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [composerOpen, setComposerOpen] = useState(false)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [builderOpen, setBuilderOpen] = useState(false)
  const [schemaGenOpen, setSchemaGenOpen] = useState(false)

  const openPalette = useCallback((mode: PaletteMode) => { setPaletteMode(mode); setPaletteOpen(true) }, [])
  const closePalette = useCallback(() => setPaletteOpen(false), [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey
      if (mod && e.key === 's') { e.preventDefault(); const { activeTabId, saveTab } = useAppStore.getState(); if (activeTabId) saveTab(activeTabId); return }
      if (mod && e.key === 'k') { e.preventDefault(); openPalette('commands'); return }
      if (mod && e.key === 'p') { e.preventDefault(); openPalette('files'); return }
      if (mod && e.shiftKey && e.key === 'F') { e.preventDefault(); setActiveView('search'); if (!useAppStore.getState().isSidebarOpen) useAppStore.getState().toggleSidebar(); return }
      if (mod && e.key === '/') { e.preventDefault(); setShortcutsOpen((s) => !s); return }
      if (mod && e.key === 'b') { e.preventDefault(); useAppStore.getState().toggleSidebar(); return }
      if (mod && e.key === 'j') { e.preventDefault(); useAppStore.getState().toggleAIPanel(); return }
      if (mod && e.shiftKey && e.key === 'I') { e.preventDefault(); setComposerOpen((c) => !c); return }
      if (mod && e.shiftKey && e.key === 'G') { e.preventDefault(); setGenerateOpen((g) => !g); return }
      if (mod && e.shiftKey && e.key === 'B') { e.preventDefault(); setBuilderOpen((b) => !b); return }
      if (mod && e.shiftKey && e.key === 'D') { e.preventDefault(); setSchemaGenOpen((s) => !s); return }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [openPalette])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const unsub = useAppStore.subscribe((state) => {
      if (!state.autoSave) return
      clearTimeout(timer)
      timer = setTimeout(() => {
        const { openTabs, saveTab } = useAppStore.getState()
        openTabs.filter((t) => t.isDirty).forEach((t) => saveTab(t.id))
      }, 1000)
    })
    return () => { unsub(); clearTimeout(timer) }
  }, [])

  if (!ready) {
    return (
      <div data-theme={theme} className="pulse-root" style={{ display: 'grid', placeItems: 'center', height: '100vh', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center', opacity: 0.5 }}>
          <div className="pulse-dot" style={{ width: 16, height: 16, margin: '0 auto 12px' }} />
          <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)' }}>Loading...</div>
        </div>
      </div>
    )
  }

  if (screen === 'auth' && !user) {
    return (<div data-theme={theme} className="pulse-root"><AuthPage onBack={() => setScreen('landing')} onDone={() => {
      const currentUser = useAuthStore.getState().user
      if (currentUser?.plan === 'enterprise') setScreen('payment')
      else if (currentUser?.plan && currentUser.plan !== 'free') setScreen('dashboard')
      else setScreen('plans')
    }} /></div>)
  }

  if (!user) {
    return (
      <LandingPage onSubmit={(prompt) => {
        if (prompt === '__login__') {
          setScreen('auth')
        } else {
          localStorage.setItem('pulse-pending-prompt', prompt)
          setPendingPrompt(prompt)
          setScreen('auth')
        }
      }} />
    )
  }

  if (screen === 'payment') {
    return (<div data-theme={theme} className="pulse-root"><EnterprisePaymentPage onSuccess={() => setScreen('ide')} /></div>)
  }

  if (screen === 'plans') {
    return (<div data-theme={theme} className="pulse-root"><PlansPage onContinue={() => setScreen('dashboard')} /></div>)
  }

  if (screen === 'dashboard') {
    return (
      <DashboardPage
        onOpenIDE={(prompt) => {
          const userPlan = (useAuthStore.getState().user?.plan ?? 'free') as Plan
          if (!canUseIDE(userPlan)) {
            alert('Full IDE requires Unlimited or Enterprise plan. Upgrade in Billing.')
            return
          }
          if (prompt) {
            localStorage.setItem('pulse-pending-prompt', prompt)
            setPendingPrompt(prompt)
          }
          setScreen('ide')
        }}
        onOpenProject={(projectId) => {
          const userPlan2 = (useAuthStore.getState().user?.plan ?? 'free') as Plan
          if (!canUseIDE(userPlan2)) {
            alert('Full IDE requires Unlimited or Enterprise plan. Upgrade in Billing.')
            return
          }
          setScreen('ide')
        }}
      />
    )
  }

  const showGoals = activeView === 'goals'
  const showSearch = activeView === 'search'
  const showGit = activeView === 'git'
  const showCollab = activeView === 'collab'
  const showShared = activeView === 'shared'
  const showPulseMemory = activeView === 'pulse-memory'
  const showPackages = activeView === 'packages'
  const showSecrets = activeView === 'secrets'
  const showDatabase = activeView === 'database'
  const showTemplates = activeView === 'templates'
  const showDeploy = activeView === 'deploy'
  const showVersions = activeView === 'versions'
  const showCommunity = activeView === 'community'
  const showComments = activeView === 'comments'
  const showBilling = activeView === 'billing'
  const showCron = activeView === 'cron'
  const showAnalytics = activeView === 'analytics'
  const showThemes = activeView === 'themes'
  const showTeams = activeView === 'teams'
  const showReview = activeView === 'review'
  const showDocs = activeView === 'docs'
  const showScanner = activeView === 'scanner'
  const showCICD = activeView === 'cicd'
  const showMarketplace = activeView === 'marketplace'
  const showDebugger = activeView === 'debugger'
  const showEnterprise = activeView === 'enterprise'
  const showComponents = activeView === 'components'
  const showCompare = activeView === 'compare'
  const showResearch = activeView === 'research'
  const showPrivacy = activeView === 'privacy'
  const showPermissions = activeView === 'permissions'
  const showStorage = activeView === 'storage'
  const showAutomations = activeView === 'automations'
  const showSEO = activeView === 'seo'
  const showValidation = activeView === 'validation'
  const showFigma = activeView === 'figma'
  const showArtifacts = activeView === 'artifacts'
  const showAgentGoals = activeView === 'agent-goals'
  const showCanvas = activeView === 'canvas'

  const editorWithPreview = isPreviewOpen ? (
    <ResizablePanel direction="horizontal" initialSize={Math.floor(window.innerWidth * 0.55)} minSize={300} maxSize={1400} storageKey="pulse-editor-width">
      <EditorArea /><PreviewPanel />
    </ResizablePanel>
  ) : (<EditorArea />)

  const editorOrCanvas = (showCanvas || showFigma) ? (showCanvas ? <ErrorBoundary name="Canvas"><CanvasPanel /></ErrorBoundary> : <ErrorBoundary name="Figma"><FigmaViewer /></ErrorBoundary>) : editorWithPreview

  const sidebarContent = showSearch
    ? <ErrorBoundary name="Search"><SearchPanel /></ErrorBoundary>
    : showGit
    ? <ErrorBoundary name="Git"><GitPanel /></ErrorBoundary>
    : showPulseMemory
    ? <ErrorBoundary name="PulseMemory"><PulseMemoryPanel /></ErrorBoundary>
    : showShared
    ? <ErrorBoundary name="Shared"><SharedPanel /></ErrorBoundary>
    : showCollab
    ? <ErrorBoundary name="Collab"><CollabPanel /></ErrorBoundary>
    : showPackages
    ? <ErrorBoundary name="Packages"><PackagePanel /></ErrorBoundary>
    : showSecrets
    ? <ErrorBoundary name="Secrets"><SecretsPanel /></ErrorBoundary>
    : showDatabase
    ? <ErrorBoundary name="Database"><DatabasePanel /></ErrorBoundary>
    : showTemplates
    ? <ErrorBoundary name="Templates"><TemplatesPanel onCreated={() => setActiveView('files')} /></ErrorBoundary>
    : showDeploy
    ? <ErrorBoundary name="Deploy"><DeployPanel /></ErrorBoundary>
    : showVersions
    ? <ErrorBoundary name="Versions"><VersionHistory /></ErrorBoundary>
    : showComments
    ? <ErrorBoundary name="Comments"><CodeComments projectId={useAppStore.getState().activeTabId ?? ''} filePath={useAppStore.getState().openTabs.find(t => t.id === useAppStore.getState().activeTabId)?.path ?? ''} /></ErrorBoundary>
    : showCron
    ? <ErrorBoundary name="Cron"><CronPanel /></ErrorBoundary>
    : showThemes
    ? <ErrorBoundary name="Themes"><ThemeMarketplace /></ErrorBoundary>
    : showTeams
    ? <ErrorBoundary name="Teams"><TeamWorkspaces /></ErrorBoundary>
    : showReview
    ? <ErrorBoundary name="Review"><CodeReviewPanel onClose={() => setActiveView('files')} /></ErrorBoundary>
    : showDocs
    ? <ErrorBoundary name="Docs"><DocGenPanel onClose={() => setActiveView('files')} /></ErrorBoundary>
    : showScanner
    ? <ErrorBoundary name="Scanner"><SecurityScannerPanel onClose={() => setActiveView('files')} /></ErrorBoundary>
    : showCICD
    ? <ErrorBoundary name="CICD"><CICDPanel /></ErrorBoundary>
    : showMarketplace
    ? <ErrorBoundary name="Marketplace"><MarketplacePanel /></ErrorBoundary>
    : showComponents
    ? <ErrorBoundary name="Components"><ComponentLibraryPanel /></ErrorBoundary>
    : showDebugger
    ? <ErrorBoundary name="Debugger"><DebuggerPanel /></ErrorBoundary>
    : showEnterprise
    ? <ErrorBoundary name="Enterprise"><EnterprisePanel /></ErrorBoundary>
    : showCompare
    ? <ErrorBoundary name="Compare"><ModelComparePanel /></ErrorBoundary>
    : showResearch
    ? <ErrorBoundary name="Research"><ResearchPanel /></ErrorBoundary>
    : showPrivacy
    ? <ErrorBoundary name="Privacy"><PrivacyPanel /></ErrorBoundary>
    : showPermissions
    ? <ErrorBoundary name="Permissions"><AgentPermissionsPanel /></ErrorBoundary>
    : showStorage
    ? <ErrorBoundary name="Storage"><StoragePanel /></ErrorBoundary>
    : showAutomations
    ? <ErrorBoundary name="Automations"><AutomationsPanel /></ErrorBoundary>
    : showSEO
    ? <ErrorBoundary name="SEO"><SEOPanel /></ErrorBoundary>
    : showValidation
    ? <ErrorBoundary name="Validation"><ValidationPanel /></ErrorBoundary>
    : showArtifacts
    ? <ErrorBoundary name="Artifacts"><ArtifactsPanel /></ErrorBoundary>
    : showAgentGoals
    ? <ErrorBoundary name="AQAgent"><AQRenderer /></ErrorBoundary>
    : showFigma
    ? <ErrorBoundary name="FigmaSidebar"><FigmaImportPanel /></ErrorBoundary>
    : <ErrorBoundary name="Sidebar"><Sidebar /></ErrorBoundary>

  // ═══════════════════════════════════════════
  // MOBILE LAYOUT — one panel at a time + bottom tab bar
  // ═══════════════════════════════════════════
  if (isMobile) {
    return (
      <div data-theme={theme} className="pulse-root">
        <TitleBar onOpenSettings={() => setSettingsOpen(true)} onOpenCommandPalette={() => openPalette('commands')} onOpenFileSearch={() => openPalette('files')} onBackToDashboard={() => setScreen('dashboard')} />

        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {showGoals ? (
            <div style={{ flex: 1, overflow: 'hidden' }}><ErrorBoundary name="Goals"><GoalsPage /></ErrorBoundary></div>
          ) : (
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {mobileTab === 'files' && <div style={{ height: '100%' }}>{sidebarContent}</div>}
              {mobileTab === 'editor' && (
                <ErrorBoundary name="Editor">
                  <CrashTrigger name="Editor" />
                  {editorOrCanvas}
                </ErrorBoundary>
              )}
              {mobileTab === 'ai' && <AIPanel onOpenGoals={() => setActiveView('goals')} />}
            </div>
          )}

          {isTerminalOpen && <ResizableTerminal />}
        </div>

        {/* Mobile bottom tab bar */}
        {!showGoals && (
          <div style={{
            display: 'flex', height: 44, background: 'var(--bg-1)',
            borderTop: '1px solid var(--border)', flexShrink: 0,
          }}>
            {([
              { id: 'files' as MobileTab, icon: '\u25a4', label: 'Files' },
              { id: 'editor' as MobileTab, icon: '\u270e', label: 'Editor' },
              { id: 'ai' as MobileTab, icon: '\u2726', label: 'AI' },
            ] as const).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setMobileTab(tab.id)}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 2, background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 14,
                  color: mobileTab === tab.id ? 'var(--accent)' : 'var(--text-2)',
                  transition: 'color 0.12s',
                }}
              >
                <span>{tab.icon}</span>
                <span style={{ fontSize: 8, fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '0.05em' }}>{tab.label}</span>
              </button>
            ))}
            <button
              onClick={() => setActiveView(showGoals ? 'files' : 'goals')}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 2, background: 'none', border: 'none',
                cursor: 'pointer', fontSize: 14,
                color: showGoals ? 'var(--accent)' : 'var(--text-2)',
              }}
            >
              <span>{'\u25ce'}</span>
              <span style={{ fontSize: 8, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>Goals</span>
            </button>
            <button
              onClick={() => setTerminalOpen((t) => !t)}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 2, background: 'none', border: 'none',
                cursor: 'pointer', fontSize: 14,
                color: isTerminalOpen ? 'var(--accent)' : 'var(--text-2)',
              }}
            >
              <span>{'\u2328'}</span>
              <span style={{ fontSize: 8, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>Term</span>
            </button>
          </div>
        )}

        <CommandPalette isOpen={paletteOpen} mode={paletteMode} onClose={closePalette} onOpenSettings={() => setSettingsOpen(true)} />
        {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} />}
        {shortcutsOpen && <ShortcutsModal onClose={() => setShortcutsOpen(false)} />}
      </div>
    )
  }

  // ═══════════════════════════════════════════
  // DESKTOP LAYOUT — side-by-side panels
  // ═══════════════════════════════════════════
  return (
    <div data-theme={theme} className="pulse-root" onPointerMove={(e) => { document.documentElement.style.setProperty('--mx', `${e.clientX}px`); document.documentElement.style.setProperty('--my', `${e.clientY}px`) }}>
      {/* Same background effects as landing/login */}
      <div className="app-bg-gradient" style={{ position: 'fixed', zIndex: 0 }}>
        <div className="app-bg-gradient-top" />
        <div className="app-bg-gradient-bottom" />
      </div>
      <div className="cursor-spot" />

      <TitleBar onOpenSettings={() => setSettingsOpen(true)} onOpenCommandPalette={() => openPalette('commands')} onOpenFileSearch={() => openPalette('files')} onBackToDashboard={() => setScreen('dashboard')} />

      <div className="pulse-main">
        <ActivityBar
          activeView={activeView} onViewChange={setActiveView}
          isPreviewOpen={isPreviewOpen} onTogglePreview={() => setPreviewOpen((p) => !p)}
          isTerminalOpen={isTerminalOpen} onToggleTerminal={() => setTerminalOpen((t) => !t)}
        />

        {showGoals ? (
          <div style={{ flex: 1, overflow: 'hidden' }}><ErrorBoundary name="Goals"><GoalsPage /></ErrorBoundary></div>
        ) : showCommunity ? (
          <div style={{ flex: 1, overflow: 'hidden' }}><ErrorBoundary name="Community"><CommunityPage /></ErrorBoundary></div>
        ) : showBilling ? (
          <div style={{ flex: 1, overflow: 'hidden' }}><ErrorBoundary name="Billing"><BillingDashboard /></ErrorBoundary></div>
        ) : showAnalytics ? (
          <div style={{ flex: 1, overflow: 'hidden' }}><ErrorBoundary name="Analytics"><AnalyticsDashboard /></ErrorBoundary></div>
        ) : (
          <>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
              <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                <div className="pulse-center">
                  <ErrorBoundary name="Editor">
                  <CrashTrigger name="Editor" />
                   {showCanvas ? (
                     <CanvasPanel />
                   ) : showFigma ? (
                     <ResizablePanel direction="horizontal" initialSize={220} minSize={140} maxSize={480} storageKey="pulse-sidebar-width">
                       {sidebarContent}
                       <FigmaViewer />
                     </ResizablePanel>
                   ) : isSidebarOpen ? (
                     <ResizablePanel direction="horizontal" initialSize={220} minSize={140} maxSize={480} storageKey="pulse-sidebar-width">
                       {sidebarContent}
                       {editorWithPreview}
                     </ResizablePanel>
                   ) : (
                     editorWithPreview
                   )}
                   </ErrorBoundary>
                </div>

                {isAIPanelOpen && (
                  <div style={{ width: 320, flexShrink: 0, borderLeft: '1px solid var(--border)', background: '#000000' }}>
                    <AIPanel onOpenGoals={() => setActiveView('goals')} />
                  </div>
                )}
              </div>

              {isTerminalOpen && <ResizableTerminal />}
            </div>
          </>
        )}
      </div>

      <StatusBar />

      <CommandPalette isOpen={paletteOpen} mode={paletteMode} onClose={closePalette} onOpenSettings={() => setSettingsOpen(true)} />
      {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} />}
      {shortcutsOpen && <ShortcutsModal onClose={() => setShortcutsOpen(false)} />}
      {composerOpen && <ComposerPanel onClose={() => setComposerOpen(false)} />}
      {generateOpen && <GenerateAppPanel onClose={() => setGenerateOpen(false)} />}
      {builderOpen && <VisualBuilderPanel onClose={() => setBuilderOpen(false)} />}
      {schemaGenOpen && <SchemaGenPanel onClose={() => setSchemaGenOpen(false)} />}
    </div>
  )
}

function EnterprisePaymentPage({ onSuccess }: { onSuccess: () => void }) {
  const user = useAuthStore(s => s.user)
  const logout = useAuthStore(s => s.logout)

  const handlePay = () => {
    // In production: redirect to Stripe checkout
    // window.location.href = '/api/stripe/checkout?plan=enterprise'
    // For now: simulate payment success
    alert('In production, this redirects to Stripe checkout ($99/mo).\n\nFor development, click OK to simulate payment success.')
    onSuccess()
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 420 }}>
        <div className="auth-logo">
          <span className="pulse-dot" style={{ width: 12, height: 12 }} />
          <span className="auth-logo-text">PULSE</span>
        </div>
        <h2 className="auth-title">Enterprise Plan</h2>
        <p className="auth-sub">Complete payment to activate your enterprise workspace</p>

        <div style={{ width: '100%', background: 'var(--bg-2)', border: '1px solid var(--border-2)', borderRadius: 10, padding: 16, marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-0)', fontWeight: 700 }}>Enterprise</span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 900, color: 'var(--accent)' }}>$99<span style={{ fontSize: 12, color: 'var(--text-2)' }}>/mo</span></span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-1)' }}>
            <div>✓ SSO (Google, Okta, Azure AD)</div>
            <div>✓ Unlimited team members</div>
            <div>✓ Audit logs & compliance</div>
            <div>✓ Unlimited AI requests</div>
            <div>✓ 100 GB storage</div>
            <div>✓ Priority support</div>
            <div>✓ Custom domain</div>
          </div>
        </div>

        <button onClick={handlePay} style={{
          width: '100%', marginTop: 16, padding: '12px', borderRadius: 8, border: 'none',
          background: 'var(--accent)', color: '#fff', cursor: 'pointer',
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, letterSpacing: '0.04em',
        }}>
          Pay $99/mo — Activate Enterprise
        </button>

        <div style={{ marginTop: 12, fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
          Logged in as {user?.email}
          <br />
          <button onClick={() => logout()} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, marginTop: 4 }}>Use a different account</button>
        </div>
      </div>
    </div>
  )
}

function ShortcutsModal({ onClose }: { onClose: () => void }) {
  const shortcuts = [
    { keys: 'Ctrl+S', desc: 'Save file' },
    { keys: 'Ctrl+K', desc: 'Command palette' },
    { keys: 'Ctrl+P', desc: 'File search' },
    { keys: 'Ctrl+Shift+F', desc: 'Search in files' },
    { keys: 'Ctrl+B', desc: 'Toggle sidebar' },
    { keys: 'Ctrl+J', desc: 'Toggle AI panel' },
    { keys: 'Ctrl+/', desc: 'Keyboard shortcuts' },
    { keys: 'Tab', desc: 'Accept ghost text completion' },
    { keys: 'Escape', desc: 'Dismiss ghost text / close modal' },
    { keys: 'Enter', desc: 'Send AI message' },
    { keys: 'Shift+Enter', desc: 'New line in AI input' },
  ]

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="shortcuts-overlay" onClick={onClose}>
      <div className="shortcuts-modal" onClick={(e) => e.stopPropagation()}>
        <div className="shortcuts-modal__header">
          <span>Keyboard Shortcuts</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', display: 'flex' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="shortcuts-modal__body">
          {shortcuts.map((s) => (
            <div key={s.keys} className="shortcuts-modal__row">
              <span>{s.desc}</span>
              <kbd>{s.keys}</kbd>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
